/* eslint-disable no-console */
import { autoEffect, clearEffect } from '@risingstack/react-easy-state';

import { SUBSCRIBE_OHLC_CANDEL, UNSUBSCRIBE_OHLC_CANDEL } from 'actionTypes/socket';
import { TOGGLE_PLACE_ORDER_SECTION } from 'actionTypes/tradingView';
// import {
//   LibrarySymbolInfo,
//   PeriodParams,
//   Bar,
// } from 'charting_library_trading_platform/charting_library';
import { getChange } from 'components/Header/partials/get_change';
import { CHARTS_API } from 'constants/api';
import { STORAGE_KEYS } from 'constants/enums';
import { constructQueryString } from 'helpers/utils';
import store from 'store';
import StoreProxy from 'storeProxy';
import { LastTrade } from 'types/ITrade';
import { setTradingViewSubscriptionData } from 'variableStore/actions/tradingview';
import variableStore from 'variableStore/store';

import { calculateFromTime, calculateToTime, getChartCacheTTL } from './helper';

const quotesSubscriptions = new Map<string, any>();

const requester = async (url: string) => {
  try {
    const response = await fetch(url);
    const responseTest = await response.text();
    return JSON.parse(responseTest).result;
  } catch (error) {
    console.error('[requester]: Method call error', error);
    return null;
  }
};
/**
 * ref: https://www.tradingview.com/charting-library-docs/latest/connecting_data/Datafeed-API
 */
export type PeriodParamsWithOptionalCountback = Omit<any, 'countBack'> & {
  countBack?: number;
};

const getSupportedResolution = data => {
  if (data?.supported_resolutions) {
    return data.supported_resolutions;
  }
  return ['1', '5', '15', '30', '60', '10', '45', '90', '180', '720', 'D', 'W', 'M'];
};

const datafeed = (contractType?: string, isNewTab?: boolean) => {
  const lastBarsData = new Map();
  return {
    onReady: async (callback: any) => {
      console.log('DEBUG [onReady]: Method call');
      const configurationData = await requester(CHARTS_API.CONFIG);
      setTimeout(
        () =>
          callback({
            ...configurationData,
            supported_resolutions: getSupportedResolution(configurationData),
          }),
        0
      );
    },
    searchSymbols: async (
      userInput: string,
      exchange: string,
      symbolType: string,
      onResultReadyCallback: any
    ) => {
      store.dispatch({
        type: TOGGLE_PLACE_ORDER_SECTION,
        payload: false,
      });
      const symbolTypeParam = () => {
        if (isNewTab) {
          return 'futures,mark_futures,call_options,put_options,mark_call_options,mark_put_options,tracker';
        }
        if (contractType === 'futures' || contractType === 'perpetual_futures') {
          return 'futures,mark_futures';
        }
        if (contractType === 'call_options' || contractType === 'put_options') {
          return 'call_options,put_options,mark_call_options,mark_put_options,spot,futures,mark_futures';
        }
        if (contractType === 'move_options') {
          return 'futures,mark_futures,call_options,put_options,mark_call_options,mark_put_options,move_options,mark_move_options';
        }
        if (contractType === 'tracker') {
          return 'tracker,mark_tracker';
        }
        return 'spot';
      };
      const queryString = {
        query: userInput,
        exchange,
        symbolType: symbolTypeParam(),
      };
      const api = constructQueryString(CHARTS_API.COMPARE_SYMBOL, queryString);
      const data = await requester(api);
      console.log('DEBUG [searchSymbols]: Method call', {
        userInput,
        exchange,
        symbolType,
        data,
      });
      setTimeout(() => {
        onResultReadyCallback(data);
      }, 0);
    },
    resolveSymbol: async (
      symbolName: string,
      onSymbolResolvedCallback: any,
      onResolveErrorCallback: any
    ) => {
      try {
        const symbol = symbolName.includes('Delta:')
          ? symbolName.split(':')[1]
          : symbolName;
        const data = await requester(`${CHARTS_API.SEARCH_SYMBOL}?symbol=${symbol}`);
        if (data) {
          const symbolInfo: any = {
            ...data,
            name: data.name,
            base_name: `Delta:${data.name}`,
            full_name: data.name,
            listed_exchange: 'Delta',
            exchange: 'Delta',
            currency_code: data.currency_code ?? data['currency-code'],
            original_currency_code: data.currency_code ?? data['currency-code'],
            // unit_id: response.unit_id ?? response['unit-id'],
            // original_unit_id: response.original_unit_id ?? response['original-unit-id'],
            // unit_conversion_types:
            // response.unit_conversion_types ?? response['unit-conversion-types'],
            has_intraday: data.has_intraday ?? data['has-intraday'] ?? true,
            visible_plots_set: data.visible_plots_set ?? data['visible-plots-set'],
            minmov: data.minmovement ?? data.minmov ?? 1,
            minmove2: data.minmovement2 ?? data.minmove2,
            pricescale: Math.round(Number(data.pricescale ?? data['price-scale'] ?? 10)),
            session: data.session ?? data['session-regular'],
            session_holidays: data.session_holidays ?? data['session-holidays'],
            supported_resolutions: getSupportedResolution(data),
            has_daily: data.has_daily ?? data['has-daily'] ?? true,
            intraday_multipliers: data.intraday_multipliers ??
              data['intraday-multipliers'] ?? ['1', '5', '15', '30', '60'],
            has_weekly_and_monthly:
              data.has_weekly_and_monthly ?? data['has-weekly-and-monthly'],
            has_empty_bars: data.has_empty_bars ?? data['has-empty-bars'],
            volume_precision: data.volume_precision ?? data['volume-precision'],
            format: data.format ?? 'price',
          };
          setTimeout(() => {
            onSymbolResolvedCallback(symbolInfo);
          }, 0);
        } else {
          setTimeout(() => {
            onResolveErrorCallback('Not found');
          }, 0);
        }
      } catch (error) {
        console.error('[resolveSymbol]: Method call error', error);
        onResolveErrorCallback('Not found');
      }
    },
    getBars: async (
      symbolInfo: any,
      resolution: string,
      periodParams: PeriodParamsWithOptionalCountback,
      onHistoryCallback: any,
      onErrorCallback: any
    ) => {
      try {
        const apiResolution = resolution === '1D' ? 'D' : resolution;
        const chartCacheTTL = getChartCacheTTL(apiResolution);
        const toTime = calculateToTime({
          to: periodParams.to,
          resolution: apiResolution,
        });
        const fromTime = calculateFromTime({
          countBack: periodParams.countBack,
          to: periodParams.to,
          resolution: apiResolution,
        });
        const data = await requester(
          `${CHARTS_API.HISTORY_SYMBOL}?symbol=${symbolInfo.name}&resolution=${apiResolution}&from=${fromTime}&to=${toTime}&cache_ttl=${chartCacheTTL}`
        );
        if (data && data.t.length > 0) {
          let bars: any[];
          const uniqueBarsMap = new Map<number, any>();

          data.t.forEach((time: number, index: number) => {
            const barTime = time * 1000;
            if (barTime >= fromTime * 1000 && barTime < toTime * 1000) {
              uniqueBarsMap.set(barTime, {
                time: barTime, // Convert to milliseconds
                close: parseFloat(data.c[index]),
                open: parseFloat(data.o[index]),
                high: parseFloat(data.h[index]),
                low: parseFloat(data.l[index]),
                volume: parseFloat(data.v[index]),
              });
            }
          });
          bars = Array.from(uniqueBarsMap.values());

          if (periodParams.firstDataRequest) {
            lastBarsData.set(symbolInfo.name, { ...bars[bars.length - 1] });
          }

          if (periodParams.countBack && bars.length < periodParams.countBack) {
            const additionalBarsNeeded = periodParams.countBack - bars.length;
            const earliestBarTime =
              bars.length > 0 ? bars[0].time / 1000 : periodParams.from; // Convert back to seconds
            const additionalBars = await requester(
              `${CHARTS_API.HISTORY_SYMBOL}?symbol=${
                symbolInfo.name
              }&resolution=${apiResolution}&from=${
                earliestBarTime - additionalBarsNeeded * 60 * 60 * 24
              }&to=${earliestBarTime}`
            );
            if (additionalBars && additionalBars.t.length > 0) {
              additionalBars.t.forEach((time: number, index: number) => {
                const barTime = time * 1000;
                uniqueBarsMap.set(barTime, {
                  time: barTime, // Convert to milliseconds
                  close: parseFloat(additionalBars.c[index]),
                  open: parseFloat(additionalBars.o[index]),
                  high: parseFloat(additionalBars.h[index]),
                  low: parseFloat(additionalBars.l[index]),
                  volume: parseFloat(additionalBars.v[index]),
                });
              });
              bars = Array.from(uniqueBarsMap.values());
            }
          }

          bars.sort((a, b) => a.time - b.time);

          if (bars.length === 0) {
            setTimeout(() => {
              onHistoryCallback([], { noData: true });
            }, 0);
          } else {
            setTimeout(() => {
              onHistoryCallback(bars, { noData: false });
            }, 0);
          }
        } else {
          onHistoryCallback([], { noData: true });
        }
      } catch (error) {
        console.error('[getBars]: Method call error', error);
        onErrorCallback(error);
      }
    },
    subscribeBars: (
      symbolInfo: any,
      resolution: string,
      onRealtimeCallback: any,
      subscribeUID: string,
      onResetCacheNeededCallback: any
    ) => {
      // clearTradingViewSubscriptionData();
      const symbol = symbolInfo.name;

      const newSub = {
        symbol,
        resolution: resolution === '1D' ? 'D' : resolution,
        symbolInfo,
        lastBar: lastBarsData.get(symbol),
        onRealtimeCallback,
        subscribeUID,
        onResetCacheNeededCallback,
      };

      setTradingViewSubscriptionData(newSub);
      window.sessionStorage.setItem(
        STORAGE_KEYS.CHART_LAST_SUBSCRIPTION_ID,
        subscribeUID
      );
      store.dispatch({
        type: SUBSCRIBE_OHLC_CANDEL,
        payload: {
          symbol,
          resolution: resolution === '1D' ? 'D' : resolution,
          subscribeUID,
        },
      });
    },
    unsubscribeBars: (subscribeUID: string) => {
      store.dispatch({
        type: UNSUBSCRIBE_OHLC_CANDEL,
        payload: {
          subscribeUID,
        },
      });
    },

    getQuotes(symbols: string[], onDataCallback: any, onErrorCallback: any) {
      const data: any = [];
      const allProducts = Object.values(StoreProxy.getState().trade.products);
      const markEnabledSymbol = {};
      symbols
        .map(symbol => {
          if (symbol.startsWith('MARK:')) {
            const symbolWithoutMark = symbol.slice(5);
            markEnabledSymbol[symbolWithoutMark] = true;
            return symbolWithoutMark;
          }
          return symbol;
        })
        .forEach(symbol => {
          const product = allProducts.find(productItem => productItem.symbol === symbol);
          if (!product) {
            onErrorCallback('no product found');
            return;
          }
          const { tickerData } = variableStore;
          const bestAsk = tickerData.bestAsk[String(product.id)];
          const bestBid = tickerData.bestBuy[String(product.id)];
          const lastTradePrice = (variableStore.lastTrade as LastTrade)?.price;
          const openPrice = tickerData.tickerObj[symbol]?.open;
          const changePercentage = Number(
            getChange(openPrice, lastTradePrice).change_value
          );
          if (bestAsk || bestBid || lastTradePrice) {
            data.push({
              n: symbol,
              s: 'ok',
              v: {
                ask: bestAsk ?? 0,
                bid: bestBid ?? 0,
                spread: bestAsk && bestBid ? Math.abs(bestAsk - bestBid) : 0,
                lp: Number(lastTradePrice),
                chp: changePercentage,
                ch: openPrice ? Number(lastTradePrice) - openPrice : undefined,
              },
            });

            if (markEnabledSymbol[symbol]) {
              data.push({
                n: `MARK:${symbol}`,
                s: 'ok',
                v: {
                  ask: bestAsk ?? 0,
                  bid: bestBid ?? 0,
                  spread: bestAsk && bestBid ? Math.abs(bestAsk - bestBid) : 0,
                  lp: Number(lastTradePrice),
                  chp: changePercentage,
                  ch: openPrice ? Number(lastTradePrice) - openPrice : undefined,
                },
              });
            }
          }
        });
      setTimeout(() => onDataCallback(data), 0);
    },

    subscribeQuotes(
      symbols: string[],
      fastSymbols: string[],
      onRealtimeCallback: any,
      listenerGUID: string
    ) {
      const allProducts = Object.values(StoreProxy.getState().trade.products);
      const allSymbols = symbols.concat(fastSymbols);
      const markEnabledSymbol = {};
      const allSymbolsToBeConsidered = allSymbols.map(symbol => {
        if (symbol.startsWith('MARK:')) {
          const symbolWithoutMark = symbol.slice(5);
          markEnabledSymbol[symbolWithoutMark] = true;
          return symbolWithoutMark;
        }
        return symbol;
      });
      const uniqueSymbolsToBeConsidered = [...new Set(allSymbolsToBeConsidered)];
      const productIds = uniqueSymbolsToBeConsidered
        .map(symbol => {
          const product = allProducts.find(productItem => productItem.symbol === symbol);
          return product ? String(product.id) : null;
        })
        .filter(Boolean);

      if (productIds.length === 0) {
        console.warn(
          'subscribeQuotes: No valid product IDs found for symbols',
          allSymbols
        );
        return;
      }

      const effect = autoEffect(() => {
        const data: any = [];
        let hasChanges = false;

        allSymbols.forEach((symbol: string) => {
          const product = allProducts.find(productItem => productItem.symbol === symbol);
          if (!product) return;
          const productId = String(product.id);
          const bestAsk = variableStore.tickerData.bestAsk[productId];
          const bestBid = variableStore.tickerData.bestBuy[productId];
          const lastTradePrice = (variableStore.lastTrade as LastTrade)?.price;
          const openPrice = variableStore.tickerData.tickerObj[symbol]?.open;
          const changePercentage = Number(
            getChange(openPrice, lastTradePrice).change_value
          );
          if (bestAsk || bestBid || lastTradePrice) {
            hasChanges = true;
            data.push({
              n: symbol,
              s: 'ok',
              v: {
                ask: bestAsk ?? 0,
                bid: bestBid ?? 0,
                spread: bestAsk && bestBid ? Math.abs(bestAsk - bestBid) : 0,
                lp: Number(lastTradePrice),
                chp: changePercentage,
                ch: openPrice ? Number(lastTradePrice) - openPrice : undefined,
              },
            });

            if (markEnabledSymbol[symbol]) {
              data.push({
                n: `MARK:${symbol}`,
                s: 'ok',
                v: {
                  ask: bestAsk ?? 0,
                  bid: bestBid ?? 0,
                  spread: bestAsk && bestBid ? Math.abs(bestAsk - bestBid) : 0,
                  lp: Number(lastTradePrice),
                  chp: changePercentage,
                  ch: openPrice ? Number(lastTradePrice) - openPrice : undefined,
                },
              });
            }
          }
        });

        // Only call the callback if there are actual changes
        if (hasChanges && data.length > 0) {
          setTimeout(() => {
            try {
              onRealtimeCallback(data);
            } catch (error) {
              console.error('Error in subscribeQuotes onRealtimeCallback', error);
            }
          }, 0);
        }
      });
      quotesSubscriptions.set(listenerGUID, effect);
    },

    unsubscribeQuotes(listenerGUID: string) {
      const effect = quotesSubscriptions.get(listenerGUID);
      if (effect) {
        clearEffect(effect);
        quotesSubscriptions.delete(listenerGUID);
      }
    },
  };
};

export default datafeed;
